import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Pagination,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { useKeycloak } from '@react-keycloak/web';
import ApiConfig from '../../../utils/services/apiConfig';
import { formatLocalDateTime } from '../../../utils/dateTime';
import { useLatestRef, useMountEffect } from '../../../utils/hooks';

const AUTH_METHODS = [
  'none',
  'api_key',
  'bearer_token',
  'basic',
  'oauth2_client_credentials',
  'custom',
];

const WORKFLOW_TYPES = ['status', 'command', 'media', 'event', 'custom'];
const STEP_TYPES = ['http_request', 'integration_action', 'transform', 'delay', 'custom'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const emptyJson = '{}';

const readResponse = (response, fallback) => {
  if (!response) return fallback;
  if (Array.isArray(response)) return response;
  if (response.RobotResponse !== undefined) return response.RobotResponse;
  if (response.results?.RobotResponse !== undefined) return response.results.RobotResponse;
  return fallback;
};

const readTotalCount = (response, fallback) => {
  if (typeof response?.count === 'number') return response.count;
  if (typeof response?.results?.count === 'number') return response.results.count;
  return fallback;
};

const stringifyJson = (value) => JSON.stringify(value || {}, null, 2);

const parseJson = (value, fieldName) => {
  try {
    const parsed = JSON.parse(value || emptyJson);
    if (parsed === null || Array.isArray(parsed) || typeof parsed !== 'object') {
      throw new Error(`${fieldName} must be a JSON object.`);
    }
    return parsed;
  } catch (error) {
    throw new Error(`${fieldName}: ${error.message}`);
  }
};

const OUTPUT_MAPPING_ALIAS_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
let nextArrayMappingFieldId = 0;

const createArrayMappingField = (overrides = {}) => ({
  id: `array-output-field-${nextArrayMappingFieldId += 1}`,
  outputKey: '',
  valueType: 'path',
  value: '',
  defaultValue: '',
  ...overrides,
});

const parseBuilderLiteral = (value) => {
  const trimmedValue = String(value ?? '').trim();
  if (!trimmedValue) return '';

  try {
    return JSON.parse(trimmedValue);
  } catch (error) {
    return value;
  }
};

const setNestedMappingValue = (target, outputKey, value) => {
  const parts = outputKey.split('.').map((part) => part.trim());
  if (parts.some((part) => !part)) {
    throw new Error(`Output key "${outputKey}" contains an empty path segment.`);
  }

  let current = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      current[part] = value;
      return;
    }
    if (!current[part] || typeof current[part] !== 'object' || Array.isArray(current[part])) {
      current[part] = {};
    }
    current = current[part];
  });
};

const userNameFrom = (keycloak) => keycloak.tokenParsed?.preferred_username || 'system';

const payloadDefaults = (userName) => ({
  RobotTableId: '',
  PayloadType: '',
  Name: '',
  Url: '',
  Active: true,
  Ssl: true,
  Deleted: false,
  CreatedBy: userName,
  UpdatedBy: userName,
});

const authDefaults = (payloadId, userName) => ({
  RobotPayloadId: payloadId,
  AuthMethod: 'none',
  TokenUrl: '',
  Username: '',
  Password: '',
  ClientId: '',
  ClientSecret: '',
  ApiKey: '',
  HeaderName: '',
  HeaderPrefix: '',
  Scope: '',
  TokenRequestHeaders: emptyJson,
  TokenRequestBody: emptyJson,
  TokenRequestParams: emptyJson,
  AccessTokenJsonPath: 'access_token',
  ExpiresInJsonPath: '',
  Active: true,
  CreatedBy: userName,
  UpdatedBy: userName,
});

const workflowDefaults = (payloadId, userName) => ({
  RobotPayloadId: payloadId,
  Name: '',
  WorkflowType: 'command',
  Description: '',
  InputSchema: emptyJson,
  OutputSchema: emptyJson,
  ContinuousPolling: false,
  PollingInterval: 5,
  Active: true,
  CreatedBy: userName,
  UpdatedBy: userName,
});

const stepDefaults = (workflowId, stepOrder, userName) => ({
  PayloadWorkflowId: workflowId,
  IntegrationActionId: '',
  Name: '',
  StepOrder: stepOrder,
  StepType: 'http_request',
  Method: 'POST',
  Path: '',
  UseAuthentication: true,
  Headers: '{\n  "Content-Type": "application/json"\n}',
  QueryParams: emptyJson,
  Body: emptyJson,
  InputMappings: emptyJson,
  OutputMappings: emptyJson,
  SaveResponseAs: '',
  TimeoutSeconds: 30,
  RetryCount: 0,
  ContinueOnError: false,
  Active: true,
  CreatedBy: userName,
  UpdatedBy: userName,
});

function PayloadManagement() {
  const { keycloak } = useKeycloak();
  const apiConfig = useMemo(() => new ApiConfig(keycloak).api(), [keycloak]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [payloads, setPayloads] = useState([]);
  const [payloadPaginationModel, setPayloadPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [payloadTotalCount, setPayloadTotalCount] = useState(0);
  const [robots, setRobots] = useState([]);
  const [selectedPayloadId, setSelectedPayloadId] = useState('');
  const [selectedPayloadSnapshot, setSelectedPayloadSnapshot] = useState(null);
  const [authentication, setAuthentication] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [steps, setSteps] = useState([]);
  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [workflowTasksLoading, setWorkflowTasksLoading] = useState(false);
  const [workflowTaskPaginationModel, setWorkflowTaskPaginationModel] = useState({ page: 0, pageSize: 5 });
  const [workflowTaskTotalCount, setWorkflowTaskTotalCount] = useState(0);
  const [integrationActions, setIntegrationActions] = useState([]);
  const [payloadDialog, setPayloadDialog] = useState(null);
  const [authDialog, setAuthDialog] = useState(false);
  const [workflowDialog, setWorkflowDialog] = useState(null);
  const [stepDialog, setStepDialog] = useState(null);

  const userName = userNameFrom(keycloak);
  const selectedPayload = payloads.find((payload) => payload.Id === selectedPayloadId)
    || (selectedPayloadSnapshot?.Id === selectedPayloadId ? selectedPayloadSnapshot : null);
  const selectedWorkflow = workflows.find((workflow) => workflow.Id === selectedWorkflowId) || null;
  const integrationActionLabelById = useMemo(() => {
    const labels = {};
    integrationActions.forEach((action) => {
      labels[action.Id] = `${action.Name} (${action.ActionType})`;
    });
    return labels;
  }, [integrationActions]);

  const robotLabelById = useMemo(() => {
    const labels = {};
    robots.forEach((robot) => {
      labels[robot.Id] = robot.RobotId || robot.Name || robot.Id;
    });
    return labels;
  }, [robots]);

  const loadPayloads = async () => {
    setLoading(true);
    setError('');
    try {
      const [payloadResponse, robotResponse] = await Promise.all([
        apiConfig.fetchAllPayloads(
          payloadPaginationModel.page + 1,
          payloadPaginationModel.pageSize,
          searchText.toLowerCase(),
        ),
        apiConfig.fetchRobotData(0),
      ]);
      const nextPayloads = readResponse(payloadResponse, []);
      const nextTotalCount = readTotalCount(payloadResponse, nextPayloads.length);
      setPayloads(nextPayloads);
      setPayloadTotalCount(nextTotalCount);
      setRobots(readResponse(robotResponse, []));
      const selectedPayloadInPage = nextPayloads.find((payload) => payload.Id === selectedPayloadId);
      if (selectedPayloadInPage) {
        setSelectedPayloadSnapshot(selectedPayloadInPage);
      }
      if (!selectedPayloadId && nextPayloads.length > 0) {
        setSelectedPayloadId(nextPayloads[0].Id);
        setSelectedPayloadSnapshot(nextPayloads[0]);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrationActions = async () => {
    setError('');
    try {
      const response = await apiConfig.fetchAllIntegrationActions();
      setIntegrationActions(readResponse(response, []));
    } catch (err) {
      setError(String(err));
    }
  };

  const loadPayloadDetail = async (payloadId) => {
    if (!payloadId) return;
    setError('');
    setAuthentication(null);
    setWorkflows([]);
    setSelectedWorkflowId('');
    setSteps([]);
    setWorkflowTasks([]);
    try {
      const [authResponse, workflowResponse] = await Promise.all([
        apiConfig.fetchPayloadAuthenticationByPayloadId(payloadId),
        apiConfig.fetchPayloadWorkflowsByPayloadId(payloadId),
      ]);
      setAuthentication(readResponse(authResponse, null));
      const nextWorkflows = readResponse(workflowResponse, []);
      setWorkflows(nextWorkflows);
      if (nextWorkflows.length > 0) {
        setSelectedWorkflowId(nextWorkflows[0].Id);
      }
    } catch (err) {
      setError(String(err));
    }
  };

  const loadWorkflowSteps = async (workflowId) => {
    if (!workflowId) return;
    setError('');
    try {
      const stepResponse = await apiConfig.fetchPayloadWorkflowStepsByWorkflowId(workflowId);
      const nextSteps = readResponse(stepResponse, []).sort((a, b) => a.StepOrder - b.StepOrder);
      setSteps(nextSteps);
    } catch (err) {
      setError(String(err));
    }
  };

  const loadWorkflowTasks = async (workflowId, payloadId = selectedPayloadId) => {
    if (!workflowId && !payloadId) {
      setWorkflowTasks([]);
      setWorkflowTaskTotalCount(0);
      return;
    }

    const paginationModel = workflowTaskPaginationModel;
    setError('');
    setWorkflowTasksLoading(true);
    try {
      const taskResponse = workflowId
        ? await apiConfig.fetchPayloadWorkflowTasksByWorkflowId(workflowId, paginationModel.page + 1, paginationModel.pageSize)
        : await apiConfig.fetchPayloadWorkflowTasksByPayloadId(payloadId, paginationModel.page + 1, paginationModel.pageSize);

      const pagedRows = taskResponse?.results?.RobotResponse ?? taskResponse?.RobotResponse ?? taskResponse?.results ?? taskResponse ?? [];
      const nextRows = Array.isArray(pagedRows) ? pagedRows : [];
      const nextTotalCount = typeof taskResponse?.count === 'number' ? taskResponse.count : nextRows.length;
      const visibleRows = typeof taskResponse?.count === 'number'
        ? nextRows
        : nextRows.slice(
            paginationModel.page * paginationModel.pageSize,
            (paginationModel.page + 1) * paginationModel.pageSize,
          );

      setWorkflowTasks(visibleRows);
      setWorkflowTaskTotalCount(nextTotalCount);
    } catch (err) {
      setError(String(err));
    } finally {
      setWorkflowTasksLoading(false);
    }
  };

  const loadPayloadsRef = useLatestRef(loadPayloads);
  const loadPayloadDetailRef = useLatestRef(loadPayloadDetail);
  const loadWorkflowStepsRef = useLatestRef(loadWorkflowSteps);
  const loadWorkflowTasksRef = useLatestRef(loadWorkflowTasks);

  useMountEffect(() => {
    loadIntegrationActions();
  }, []);

  useEffect(() => {
    loadPayloadsRef.current();
  }, [payloadPaginationModel.page, payloadPaginationModel.pageSize, searchText, loadPayloadsRef]);

  useEffect(() => {
    loadPayloadDetailRef.current(selectedPayloadId);
  }, [selectedPayloadId, loadPayloadDetailRef]);

  useEffect(() => {
    loadWorkflowStepsRef.current(selectedWorkflowId);
  }, [selectedWorkflowId, loadWorkflowStepsRef]);

  useEffect(() => {
    if (selectedWorkflowId || selectedPayloadId) {
      loadWorkflowTasksRef.current(selectedWorkflowId, selectedPayloadId);
    }
  }, [selectedWorkflowId, selectedPayloadId, workflowTaskPaginationModel.page, workflowTaskPaginationModel.pageSize, loadWorkflowTasksRef]);

  useEffect(() => {
    setWorkflowTaskPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [selectedWorkflowId]);

  const refreshSelected = async () => {
    await loadPayloads();
    await loadIntegrationActions();
    if (selectedPayloadId) await loadPayloadDetail(selectedPayloadId);
    if (selectedWorkflowId) await loadWorkflowSteps(selectedWorkflowId);
    if (selectedWorkflowId || selectedPayloadId) await loadWorkflowTasks(selectedWorkflowId, selectedPayloadId);
  };

  const savePayload = async (payload) => {
    setSaving(true);
    setError('');
    try {
      const body = { ...payload, UpdatedBy: userName };
      let savedPayload = null;
      if (payload.Id) {
        const response = await apiConfig.updatePayload(payload.Id, body);
        savedPayload = readResponse(response, null);
      } else {
        const response = await apiConfig.createPayload({ ...body, CreatedBy: userName });
        savedPayload = readResponse(response, null);
      }
      setPayloadDialog(null);
      if (savedPayload?.Id) {
        setSelectedPayloadId(savedPayload.Id);
        setSelectedPayloadSnapshot(savedPayload);
      }
      await loadPayloads();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePayload = async (payloadId) => {
    if (!window.confirm('Delete this payload and its related configuration?')) return;
    setSaving(true);
    setError('');
    try {
      await apiConfig.deletePayload(payloadId);
      setSelectedPayloadId('');
      setSelectedPayloadSnapshot(null);
      await loadPayloads();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const saveAuthentication = async (form) => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        RobotPayloadId: selectedPayload.Id,
        TokenRequestHeaders: parseJson(form.TokenRequestHeaders, 'Token request headers'),
        TokenRequestBody: parseJson(form.TokenRequestBody, 'Token request body'),
        TokenRequestParams: parseJson(form.TokenRequestParams, 'Token request params'),
        UpdatedBy: userName,
      };
      ['Password', 'ClientSecret', 'ApiKey'].forEach((secretField) => {
        if (!body[secretField]) delete body[secretField];
      });
      if (authentication?.Id) {
        await apiConfig.updatePayloadAuthentication(authentication.Id, body);
      } else {
        await apiConfig.createPayloadAuthentication({ ...body, CreatedBy: userName });
      }
      setAuthDialog(false);
      await loadPayloadDetail(selectedPayload.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const saveWorkflow = async (form) => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        RobotPayloadId: selectedPayload.Id,
        InputSchema: parseJson(form.InputSchema, 'Input schema'),
        OutputSchema: parseJson(form.OutputSchema, 'Output schema'),
        ContinuousPolling: !!form.ContinuousPolling,
        PollingInterval: Number(form.PollingInterval || 5),
        UpdatedBy: userName,
      };
      if (form.Id) {
        await apiConfig.updatePayloadWorkflow(form.Id, body);
      } else {
        await apiConfig.createPayloadWorkflow({ ...body, CreatedBy: userName });
      }
      setWorkflowDialog(null);
      await loadPayloadDetail(selectedPayload.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Delete this workflow and its steps?')) return;
    setSaving(true);
    setError('');
    try {
      await apiConfig.deletePayloadWorkflow(workflowId);
      setSelectedWorkflowId('');
      await loadPayloadDetail(selectedPayload.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const saveStep = async (form) => {
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        PayloadWorkflowId: selectedWorkflow.Id,
        IntegrationActionId: form.IntegrationActionId || null,
        UseAuthentication: form.StepType === 'integration_action' ? false : !!form.UseAuthentication,
        Path: form.StepType === 'integration_action' ? null : form.Path,
        StepOrder: Number(form.StepOrder || 1),
        TimeoutSeconds: Number(form.TimeoutSeconds || 30),
        RetryCount: Number(form.RetryCount || 0),
        Headers: parseJson(form.Headers, 'Headers'),
        QueryParams: parseJson(form.QueryParams, 'Query params'),
        Body: parseJson(form.Body, 'Body'),
        InputMappings: parseJson(form.InputMappings, 'Input mappings'),
        OutputMappings: parseJson(form.OutputMappings, 'Output mappings'),
        UpdatedBy: userName,
      };
      if (form.Id) {
        await apiConfig.updatePayloadWorkflowStep(form.Id, body);
      } else {
        await apiConfig.createPayloadWorkflowStep({ ...body, CreatedBy: userName });
      }
      setStepDialog(null);
      await loadWorkflowSteps(selectedWorkflow.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteStep = async (stepId) => {
    if (!window.confirm('Delete this workflow step?')) return;
    setSaving(true);
    setError('');
    try {
      await apiConfig.deletePayloadWorkflowStep(stepId);
      await loadWorkflowSteps(selectedWorkflow.Id);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const payloadColumns = [
    { field: 'Name', headerName: 'Payload', flex: 0.9 },
    { field: 'PayloadType', headerName: 'Type', flex: 0.55 },
    {
      field: 'RobotTableId',
      headerName: 'Robot',
      flex: 0.6,
      valueGetter: (value) => robotLabelById[value] || value,
    },
    {
      field: 'Active',
      headerName: 'Active',
      flex: 0.35,
      renderCell: (params) => <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.6,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit payload">
            <IconButton size="small" onClick={() => setPayloadDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete payload">
            <IconButton size="small" onClick={() => deletePayload(params.row.Id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const stepColumns = [
    { field: 'StepOrder', headerName: '#', flex: 0.25 },
    { field: 'Name', headerName: 'Step', flex: 0.9 },
    { field: 'StepType', headerName: 'Type', flex: 0.55 },
    { field: 'Method', headerName: 'Method', flex: 0.35 },
    {
      field: 'Path',
      headerName: 'Target',
      flex: 0.9,
      renderCell: (params) => (
        params.row.StepType === 'integration_action'
          ? integrationActionLabelById[params.row.IntegrationActionId] || params.row.IntegrationActionId || ''
          : params.value || ''
      ),
    },
    {
      field: 'UseAuthentication',
      headerName: 'Auth',
      flex: 0.3,
      renderCell: (params) => <Chip size="small" label={params.value ? 'On' : 'Off'} />,
    },
    {
      field: 'stepActions',
      headerName: '',
      sortable: false,
      flex: 0.45,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit step">
            <IconButton size="small" onClick={() => setStepDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete step">
            <IconButton size="small" onClick={() => deleteStep(params.row.Id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const taskColumns = [
    {
      field: 'Status',
      headerName: 'Status',
      flex: 0.45,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === 'succeeded' ? 'success' : params.value === 'failed' ? 'error' : params.value === 'running' ? 'primary' : 'default'}
        />
      ),
    },
    { field: 'TriggerSource', headerName: 'Source', flex: 0.45 },
    { field: 'AttemptCount', headerName: 'Attempts', flex: 0.35 },
    {
      field: 'CreatedAt',
      headerName: 'Queued At',
      flex: 0.75,
      renderCell: (params) => formatLocalDateTime(params.value),
    },
    {
      field: 'CompletedAt',
      headerName: 'Completed At',
      flex: 0.75,
      renderCell: (params) => formatLocalDateTime(params.value),
    },
    {
      field: 'Error',
      headerName: 'Error',
      flex: 1,
      renderCell: (params) => params.value || '',
    },
  ];

  return (
    <Box p={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h4">Payload Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Create payload identities, configure authentication, and define the endpoint workflow steps used by the runner.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSelected}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPayloadDialog(payloadDefaults(userName))}>
            Create Payload
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <TextField
                size="small"
                placeholder="Search payloads"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setPayloadPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Box sx={{ height: 520 }}>
              <DataGrid
                rows={payloads}
                columns={payloadColumns}
                getRowId={(row) => row.Id}
                loading={loading}
                rowCount={payloadTotalCount}
                paginationMode="server"
                paginationModel={payloadPaginationModel}
                onPaginationModelChange={setPayloadPaginationModel}
                pageSizeOptions={[5, 10, 25, 50]}
                keepNonExistentRowsSelected
                onRowClick={(params) => {
                  setSelectedPayloadId(params.row.Id);
                  setSelectedPayloadSnapshot(params.row);
                }}
                rowSelectionModel={selectedPayloadId ? [selectedPayloadId] : []}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Paper elevation={3} sx={{ p: 2 }}>
              {selectedPayload ? (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="h5">{selectedPayload.Name}</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedPayload.Url}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip label={selectedPayload.PayloadType || 'Payload'} />
                      <Chip label={selectedPayload.Active ? 'Active' : 'Inactive'} color={selectedPayload.Active ? 'success' : 'default'} />
                      <Chip label={selectedPayload.Ssl ? 'SSL' : 'No SSL'} />
                    </Stack>
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Robot</Typography>
                      <Typography>{robotLabelById[selectedPayload.RobotTableId] || selectedPayload.RobotTableId}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Payload ID</Typography>
                      <Typography sx={{ wordBreak: 'break-all' }}>{selectedPayload.Id}</Typography>
                    </Grid>
                  </Grid>
                </Stack>
              ) : (
                <Typography color="text.secondary">Select or create a payload to configure its endpoints.</Typography>
              )}
            </Paper>

            {selectedPayload && (
              <Paper elevation={3} sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={2}>
                  <Box>
                    <Typography variant="h6">Authentication</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Secrets are write-only; leave secret fields blank when editing to keep the stored values.
                    </Typography>
                  </Box>
                  <Button variant="outlined" startIcon={<KeyIcon />} onClick={() => setAuthDialog(true)}>
                    {authentication ? 'Edit Auth' : 'Configure Auth'}
                  </Button>
                </Stack>
                {authentication ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={authentication.AuthMethod} color="primary" />
                    <Chip label={authentication.Active ? 'Active' : 'Inactive'} />
                    {authentication.TokenUrl && <Chip label={`Token: ${authentication.TokenUrl}`} />}
                    {authentication.HeaderName && <Chip label={`Header: ${authentication.HeaderName}`} />}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No authentication configured.</Typography>
                )}
              </Paper>
            )}

            {selectedPayload && (
              <Paper elevation={3} sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={2}>
                  <Box>
                    <Typography variant="h6">Payload Workflows</Typography>
                    <Typography variant="body2" color="text.secondary">
                      A workflow is the operation. Its steps are the ordered payload endpoints to call.
                    </Typography>
                  </Box>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWorkflowDialog(workflowDefaults(selectedPayload.Id, userName))}>
                    Add Workflow
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                  {workflows.map((workflow) => (
                    <Chip
                      key={workflow.Id}
                      label={`${workflow.Name} (${workflow.WorkflowType}${workflow.ContinuousPolling ? `, polls ${workflow.PollingInterval || 5}s` : ''})`}
                      color={workflow.Id === selectedWorkflowId ? 'primary' : 'default'}
                      onClick={() => setSelectedWorkflowId(workflow.Id)}
                      onDelete={() => deleteWorkflow(workflow.Id)}
                    />
                  ))}
                  {workflows.length === 0 && <Typography color="text.secondary">No workflows configured.</Typography>}
                </Stack>

                {selectedWorkflow && (
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="subtitle1">{selectedWorkflow.Name}</Typography>
                        <Typography variant="body2" color="text.secondary">{selectedWorkflow.Description || 'No description'}</Typography>
                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                          <Chip size="small" label={selectedWorkflow.Active ? 'Active' : 'Inactive'} />
                          <Chip size="small" label={selectedWorkflow.ContinuousPolling ? `Polling every ${selectedWorkflow.PollingInterval || 5}s` : 'On demand'} />
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setWorkflowDialog(selectedWorkflow)}>
                          Edit Workflow
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SettingsEthernetIcon />}
                          onClick={() => setStepDialog(stepDefaults(selectedWorkflow.Id, steps.length + 1, userName))}
                        >
                          Add Endpoint Step
                        </Button>
                      </Stack>
                    </Stack>
                    <Box sx={{ height: 380 }}>
                      <DataGrid
                        rows={steps}
                        columns={stepColumns}
                        getRowId={(row) => row.Id}
                        pageSizeOptions={[10]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        disableRowSelectionOnClick
                      />
                    </Box>
                    <Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={1}>
                        <Box>
                          <Typography variant="subtitle1">Workflow Tasks</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Read-only execution records queued by the bridge and Celery runner.
                          </Typography>
                        </Box>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadWorkflowTasks(selectedWorkflow.Id, selectedPayload.Id)}>
                          Refresh Tasks
                        </Button>
                      </Stack>
                      <Box sx={{ height: 280 }}>
                        <DataGrid
                          rows={workflowTasks}
                          columns={taskColumns}
                          getRowId={(row) => row.Id}
                          loading={workflowTasksLoading}
                          rowCount={workflowTaskTotalCount}
                          paginationMode="server"
                          paginationModel={workflowTaskPaginationModel}
                          onPaginationModelChange={setWorkflowTaskPaginationModel}
                          pageSizeOptions={[5, 10, 25]}
                          hideFooterPagination
                          hideFooterSelectedRowCount
                          disableRowSelectionOnClick
                        />
                      </Box>
                      <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                          count={Math.max(1, Math.ceil(workflowTaskTotalCount / workflowTaskPaginationModel.pageSize))}
                          page={workflowTaskPaginationModel.page + 1}
                          onChange={(_, value) => setWorkflowTaskPaginationModel((prev) => ({ ...prev, page: value - 1 }))}
                          color="primary"
                        />
                      </Box>
                    </Box>
                  </Stack>
                )}
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>

      {saving && (
        <Box sx={{ position: 'fixed', right: 24, bottom: 24, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Saving</Typography>
        </Box>
      )}

      {payloadDialog && (
        <PayloadDialog
          open
          payload={payloadDialog}
          robots={robots}
          onClose={() => setPayloadDialog(null)}
          onSave={savePayload}
          saving={saving}
        />
      )}
      {authDialog && selectedPayload && (
        <AuthDialog
          open
          authentication={authentication}
          defaults={authDefaults(selectedPayload.Id, userName)}
          onClose={() => setAuthDialog(false)}
          onSave={saveAuthentication}
          saving={saving}
        />
      )}
      {workflowDialog && (
        <WorkflowDialog
          open
          workflow={workflowDialog}
          onClose={() => setWorkflowDialog(null)}
          onSave={saveWorkflow}
          saving={saving}
        />
      )}
      {stepDialog && (
        <StepDialog
          open
          step={stepDialog}
          integrationActions={integrationActions}
          onClose={() => setStepDialog(null)}
          onSave={saveStep}
          saving={saving}
        />
      )}
    </Box>
  );
}

function PayloadDialog({ open, payload, robots, onClose, onSave, saving }) {
  const [form, setForm] = useState(payload);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{form.Id ? 'Edit Payload' : 'Create Payload'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Name" value={form.Name || ''} onChange={(event) => update('Name', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Payload Type" value={form.PayloadType || ''} onChange={(event) => update('PayloadType', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Base URL" value={form.Url || ''} onChange={(event) => update('Url', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            {robots.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Robot</InputLabel>
                <Select label="Robot" value={form.RobotTableId || ''} onChange={(event) => update('RobotTableId', event.target.value)}>
                  {robots.map((robot) => (
                    <MenuItem key={robot.Id} value={robot.Id}>{robot.RobotId || robot.Name || robot.Id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField fullWidth label="Robot Table ID" value={form.RobotTableId || ''} onChange={(event) => update('RobotTableId', event.target.value)} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
              <FormControlLabel control={<Switch checked={!!form.Ssl} onChange={(event) => update('Ssl', event.target.checked)} />} label="SSL" />
              <FormControlLabel control={<Switch checked={!!form.Deleted} onChange={(event) => update('Deleted', event.target.checked)} />} label="Deleted" />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.Name || !form.RobotTableId} onClick={() => onSave(form)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AuthDialog({ open, authentication, defaults, onClose, onSave, saving }) {
  const initial = authentication
    ? {
        ...defaults,
        ...authentication,
        Password: '',
        ClientSecret: '',
        ApiKey: '',
        TokenRequestHeaders: stringifyJson(authentication.TokenRequestHeaders),
        TokenRequestBody: stringifyJson(authentication.TokenRequestBody),
        TokenRequestParams: stringifyJson(authentication.TokenRequestParams),
      }
    : defaults;
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{authentication ? 'Edit Authentication' : 'Configure Authentication'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Auth Method</InputLabel>
              <Select label="Auth Method" value={form.AuthMethod} onChange={(event) => update('AuthMethod', event.target.value)}>
                {AUTH_METHODS.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Token URL" value={form.TokenUrl || ''} onChange={(event) => update('TokenUrl', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Scope" value={form.Scope || ''} onChange={(event) => update('Scope', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Username" value={form.Username || ''} onChange={(event) => update('Username', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="Password" value={form.Password || ''} onChange={(event) => update('Password', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Client ID" value={form.ClientId || ''} onChange={(event) => update('ClientId', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="Client Secret" value={form.ClientSecret || ''} onChange={(event) => update('ClientSecret', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="password" label="API Key / Static Token" value={form.ApiKey || ''} onChange={(event) => update('ApiKey', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Header Name" value={form.HeaderName || ''} onChange={(event) => update('HeaderName', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Header Prefix" value={form.HeaderPrefix || ''} onChange={(event) => update('HeaderPrefix', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Access Token JSON Path" value={form.AccessTokenJsonPath || ''} onChange={(event) => update('AccessTokenJsonPath', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Expires In JSON Path" value={form.ExpiresInJsonPath || ''} onChange={(event) => update('ExpiresInJsonPath', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={7} label="Token Request Headers" helperText="Set Content-Type to multipart/form-data to send this request as multipart. The boundary is generated automatically." value={form.TokenRequestHeaders} onChange={(event) => update('TokenRequestHeaders', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={7} label="Token Request Body" helperText={'File: {"$file":{"filename":"file.jpg","content":"{{input.file_base64}}","encoding":"base64","content_type":"image/jpeg"}}'} value={form.TokenRequestBody} onChange={(event) => update('TokenRequestBody', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={7} label="Token Request Params" value={form.TokenRequestParams} onChange={(event) => update('TokenRequestParams', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkflowDialog({ open, workflow, onClose, onSave, saving }) {
  const initial = {
    ...workflow,
    InputSchema: typeof workflow.InputSchema === 'string' ? workflow.InputSchema : stringifyJson(workflow.InputSchema),
    OutputSchema: typeof workflow.OutputSchema === 'string' ? workflow.OutputSchema : stringifyJson(workflow.OutputSchema),
    ContinuousPolling: !!workflow.ContinuousPolling,
    PollingInterval: workflow.PollingInterval || 5,
  };
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{form.Id ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Name" value={form.Name || ''} onChange={(event) => update('Name', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Workflow Type</InputLabel>
              <Select label="Workflow Type" value={form.WorkflowType} onChange={(event) => update('WorkflowType', event.target.value)}>
                {WORKFLOW_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description" value={form.Description || ''} onChange={(event) => update('Description', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline minRows={8} label="Input Schema" value={form.InputSchema} onChange={(event) => update('InputSchema', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline minRows={8} label="Output Schema" value={form.OutputSchema} onChange={(event) => update('OutputSchema', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel control={<Switch checked={!!form.ContinuousPolling} onChange={(event) => update('ContinuousPolling', event.target.checked)} />} label="Continuous Polling" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Polling Interval (seconds)"
              value={form.PollingInterval}
              disabled={!form.ContinuousPolling}
              inputProps={{ min: 1 }}
              onChange={(event) => update('PollingInterval', event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.Name} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function ArrayOutputMappingBuilder({ value, onChange, integrationActionStep }) {
  const [outputName, setOutputName] = useState('waypoints');
  const [sourcePath, setSourcePath] = useState(
    integrationActionStep ? 'response.response.RobotResponse' : 'response.RobotResponse',
  );
  const [itemAlias, setItemAlias] = useState('waypoint');
  const [fields, setFields] = useState(() => [createArrayMappingField()]);
  const [builderError, setBuilderError] = useState('');

  const updateField = (id, fieldName, fieldValue) => {
    setFields((currentFields) => currentFields.map((field) => (
      field.id === id ? { ...field, [fieldName]: fieldValue } : field
    )));
  };

  const removeField = (id) => {
    setFields((currentFields) => {
      const remainingFields = currentFields.filter((field) => field.id !== id);
      return remainingFields.length ? remainingFields : [createArrayMappingField()];
    });
  };

  const loadWaypointExample = () => {
    setOutputName('waypoints');
    setSourcePath(integrationActionStep ? 'response.response.RobotResponse' : 'response.RobotResponse');
    setItemAlias('waypoint');
    setFields([
      createArrayMappingField({ outputKey: 'name', value: 'WaypointName' }),
      createArrayMappingField({ outputKey: 'x', value: 'X' }),
      createArrayMappingField({ outputKey: 'y', value: 'Y' }),
      createArrayMappingField({ outputKey: 'z', value: 'Z', defaultValue: '0' }),
      createArrayMappingField({ outputKey: 'angle', value: 'Angle', defaultValue: '0' }),
    ]);
    setBuilderError('');
  };

  const applyArrayMapping = () => {
    try {
      const normalizedOutputName = outputName.trim();
      const normalizedSourcePath = sourcePath.trim();
      const normalizedAlias = itemAlias.trim();
      if (!normalizedOutputName) throw new Error('Output parameter is required.');
      if (!normalizedSourcePath) throw new Error('Source array path is required.');
      if (!OUTPUT_MAPPING_ALIAS_PATTERN.test(normalizedAlias)) {
        throw new Error('Item alias must start with a letter or underscore and contain only letters, numbers, and underscores.');
      }

      const configuredFields = fields.filter((field) => field.outputKey.trim());
      if (!configuredFields.length) throw new Error('Add at least one output field.');

      const itemMapping = {};
      configuredFields.forEach((field) => {
        let fieldMapping;
        if (field.valueType === 'value') {
          fieldMapping = { value: parseBuilderLiteral(field.value) };
        } else {
          const relativePath = field.value.trim();
          if (!relativePath) {
            throw new Error(`Source path is required for output key "${field.outputKey}".`);
          }
          const itemPath = relativePath === normalizedAlias || relativePath.startsWith(`${normalizedAlias}.`)
            ? relativePath
            : `${normalizedAlias}.${relativePath}`;
          fieldMapping = { path: itemPath };
          if (String(field.defaultValue).trim()) {
            fieldMapping.default = parseBuilderLiteral(field.defaultValue);
          }
        }
        setNestedMappingValue(itemMapping, field.outputKey.trim(), fieldMapping);
      });

      const outputMappings = parseJson(value, 'Output Mappings');
      outputMappings[normalizedOutputName] = {
        type: 'array',
        path: normalizedSourcePath,
        as: normalizedAlias,
        item: itemMapping,
      };
      onChange(stringifyJson(outputMappings));
      setBuilderError('');
    } catch (error) {
      setBuilderError(error.message);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2">Variable-length array output</Typography>
            <Typography variant="caption" color="text.secondary">
              Loop over a response array and create one mapped object for every returned element.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={loadWaypointExample}>
            Use waypoint example
          </Button>
        </Stack>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Output parameter"
              value={outputName}
              onChange={(event) => setOutputName(event.target.value)}
              helperText="Stored in workflow outputs"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Source array path"
              value={sourcePath}
              onChange={(event) => setSourcePath(event.target.value)}
              helperText={integrationActionStep ? 'Usually response.response.RobotResponse' : 'Usually response.RobotResponse'}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Item alias"
              value={itemAlias}
              onChange={(event) => setItemAlias(event.target.value)}
              helperText={`Index: ${itemAlias || 'item'}_index`}
            />
          </Grid>
        </Grid>

        <Stack spacing={1}>
          {fields.map((field) => (
            <Grid container spacing={1} alignItems="center" key={field.id}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Output key"
                  placeholder="pose.x"
                  value={field.outputKey}
                  onChange={(event) => updateField(field.id, 'outputKey', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Value type</InputLabel>
                  <Select
                    label="Value type"
                    value={field.valueType}
                    onChange={(event) => updateField(field.id, 'valueType', event.target.value)}
                  >
                    <MenuItem value="path">Item path</MenuItem>
                    <MenuItem value="value">Fixed value</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.valueType === 'path' ? 'Path inside item' : 'Fixed value / template'}
                  placeholder={field.valueType === 'path' ? 'WaypointName' : 'navigation'}
                  value={field.value}
                  onChange={(event) => updateField(field.id, 'value', event.target.value)}
                />
              </Grid>
              <Grid item xs={10} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  disabled={field.valueType !== 'path'}
                  label="Default (JSON)"
                  placeholder="0"
                  value={field.defaultValue}
                  onChange={(event) => updateField(field.id, 'defaultValue', event.target.value)}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <Tooltip title="Remove output field">
                  <IconButton size="small" color="error" onClick={() => removeField(field.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setFields((current) => [...current, createArrayMappingField()])}>
            Add output field
          </Button>
          <Button size="small" variant="contained" onClick={applyArrayMapping}>
            Insert or update array mapping
          </Button>
        </Stack>

        {builderError && <Alert severity="error">{builderError}</Alert>}
        <Typography variant="caption" color="text.secondary">
          Output keys may use dots for nested objects. Missing or empty source arrays produce an empty array. Applying the builder updates the JSON below; advanced mappings can still be edited there.
        </Typography>
      </Stack>
    </Paper>
  );
}

function StepDialog({ open, step, integrationActions, onClose, onSave, saving }) {
  const initial = {
    ...step,
    IntegrationActionId: step.IntegrationActionId || '',
    Headers: typeof step.Headers === 'string' ? step.Headers : stringifyJson(step.Headers),
    QueryParams: typeof step.QueryParams === 'string' ? step.QueryParams : stringifyJson(step.QueryParams),
    Body: typeof step.Body === 'string' ? step.Body : stringifyJson(step.Body),
    InputMappings: typeof step.InputMappings === 'string' ? step.InputMappings : stringifyJson(step.InputMappings),
    OutputMappings: typeof step.OutputMappings === 'string' ? step.OutputMappings : stringifyJson(step.OutputMappings),
  };
  const [form, setForm] = useState(initial);
  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const isIntegrationActionStep = form.StepType === 'integration_action';
  const updateStepType = (value) => {
    setForm((prev) => ({
      ...prev,
      StepType: value,
      UseAuthentication: value === 'integration_action' ? false : prev.UseAuthentication,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{form.Id ? 'Edit Endpoint Step' : 'Add Endpoint Step'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Step Name" value={form.Name || ''} onChange={(event) => update('Name', event.target.value)} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth type="number" label="Order" value={form.StepOrder} onChange={(event) => update('StepOrder', event.target.value)} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={form.StepType} onChange={(event) => updateStepType(event.target.value)}>
                {STEP_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select label="Method" value={form.Method} disabled={isIntegrationActionStep} onChange={(event) => update('Method', event.target.value)}>
                {HTTP_METHODS.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {isIntegrationActionStep ? (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Integration Action</InputLabel>
                <Select label="Integration Action" value={form.IntegrationActionId || ''} onChange={(event) => update('IntegrationActionId', event.target.value)}>
                  {integrationActions.map((action) => (
                    <MenuItem key={action.Id} value={action.Id}>
                      {action.Name} ({action.ActionType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField fullWidth label="Endpoint Path" value={form.Path || ''} onChange={(event) => update('Path', event.target.value)} />
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Save Response As" value={form.SaveResponseAs || ''} onChange={(event) => update('SaveResponseAs', event.target.value)} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth type="number" label="Timeout" value={form.TimeoutSeconds} onChange={(event) => update('TimeoutSeconds', event.target.value)} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth type="number" label="Retries" value={form.RetryCount} onChange={(event) => update('RetryCount', event.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={!!form.UseAuthentication} disabled={isIntegrationActionStep} onChange={(event) => update('UseAuthentication', event.target.checked)} />} label="Use Auth" />
              <FormControlLabel control={<Switch checked={!!form.ContinueOnError} onChange={(event) => update('ContinueOnError', event.target.checked)} />} label="Continue On Error" />
              <FormControlLabel control={<Switch checked={!!form.Active} onChange={(event) => update('Active', event.target.checked)} />} label="Active" />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Headers" helperText="Set Content-Type to multipart/form-data to send this step as multipart. The boundary is generated automatically." value={form.Headers} onChange={(event) => update('Headers', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Query Params" value={form.QueryParams} onChange={(event) => update('QueryParams', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth multiline minRows={8} label="Body" helperText={'Multipart file: {"$file":{"filename":"file.jpg","content":"{{input.file_base64}}","encoding":"base64","content_type":"image/jpeg"}}'} value={form.Body} onChange={(event) => update('Body', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth multiline minRows={8} label="Input Mappings" value={form.InputMappings} onChange={(event) => update('InputMappings', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              minRows={8}
              label="Output Mappings"
              helperText="Scalar mappings and generated array mappings are both supported."
              value={form.OutputMappings}
              onChange={(event) => update('OutputMappings', event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <ArrayOutputMappingBuilder
              value={form.OutputMappings}
              onChange={(outputMappings) => update('OutputMappings', outputMappings)}
              integrationActionStep={isIntegrationActionStep}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !form.Name} onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PayloadManagement;
